import { useState, useEffect, useRef } from 'react';
import { 
  FUNNEL_STRATEGIES, 
  getStrategyById, 
  calculateAssetTotals,
  ASSET_LIMITS 
} from '@/config/funnelStrategies';
import { 
  FileText, 
  Mail, 
  MessageCircle, 
  Image, 
  Type,
  Clock,
  Timer,
  Sparkles,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Settings,
  Minus,
  Plus,
  Save,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FirstTimeHint } from '@/components/onboarding/OnboardingHelpers';

const StrategyStep = ({ 
  selectedStrategy, 
  selectedVariant, 
  onSelectStrategy, 
  onSelectVariant,
  onAssetsConfigured 
}) => {
  const [activeStrategy, setActiveStrategy] = useState(selectedStrategy || FUNNEL_STRATEGIES[0].id);
  const [activeVariant, setActiveVariant] = useState(selectedVariant || null);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customAssets, setCustomAssets] = useState(null);
  const tabsRef = useRef(null);
  
  // Get current strategy and variant data
  const currentStrategy = getStrategyById(activeStrategy);
  const currentVariant = currentStrategy?.variants.find(v => v.id === activeVariant);
  
  // Use custom assets if set, otherwise use default
  const currentAssets = customAssets || currentVariant?.defaultAssets;
  const assetTotals = currentAssets ? calculateAssetTotals(currentAssets) : null;

  // Sync initial strategy with parent on mount - ALWAYS ensure parent has strategy
  useEffect(() => {
    // Always sync the default strategy to parent if not already set
    if (activeStrategy && !selectedStrategy) {
      onSelectStrategy(activeStrategy);
    }
  }, [activeStrategy, selectedStrategy, onSelectStrategy]);

  // Sync with parent state
  useEffect(() => {
    if (selectedStrategy && selectedStrategy !== activeStrategy) {
      setActiveStrategy(selectedStrategy);
    }
    if (selectedVariant && selectedVariant !== activeVariant) {
      setActiveVariant(selectedVariant);
    }
  }, [selectedStrategy, selectedVariant, activeStrategy, activeVariant]);

  // Reset custom assets when variant changes
  useEffect(() => {
    if (currentVariant?.defaultAssets) {
      setCustomAssets({ ...currentVariant.defaultAssets });
      setIsCustomizing(false);
    }
  }, [activeVariant, currentVariant?.defaultAssets]);

  // Handle strategy tab click
  const handleStrategyClick = (strategyId) => {
    setActiveStrategy(strategyId);
    setActiveVariant(null);
    setCustomAssets(null);
    setIsCustomizing(false);
    onSelectStrategy(strategyId);
    onSelectVariant(null);
  };

  // Handle variant selection
  const handleVariantClick = (variantId) => {
    setActiveVariant(variantId);
    
    // IMPORTANT: Also ensure strategy is synced to parent when selecting variant
    if (activeStrategy && !selectedStrategy) {
      onSelectStrategy(activeStrategy);
    }
    
    onSelectVariant(variantId);
    
    const variant = currentStrategy?.variants.find(v => v.id === variantId);
    if (variant) {
      const assets = { ...variant.defaultAssets };
      
      // Include email names from variant if they exist
      if (variant.emails && variant.emails.length > 0) {
        assets.emailNames = variant.emails.map(e => e.nombre);
        assets.originalEmailNames = variant.emails.map(e => e.nombre);
        assets.emailsConfig = variant.emails; // Full email config with ids and moments
      }
      
      setCustomAssets(assets);
      if (onAssetsConfigured) {
        onAssetsConfigured(assets);
      }
    }
  };

  // Handle asset quantity changes
  const handleAssetChange = (assetKey, delta) => {
    if (!customAssets) return;
    
    const limits = ASSET_LIMITS[assetKey];
    if (!limits) return;
    
    // Use step for cartHours (12h increments)
    const step = limits.step || 1;
    const actualDelta = delta * step;
    const currentValue = customAssets[assetKey] || 0;
    const newValue = Math.max(limits.min, Math.min(limits.max, currentValue + actualDelta));
    const newAssets = { ...customAssets, [assetKey]: newValue };
    setCustomAssets(newAssets);
  };

  // Save customization
  const saveCustomization = () => {
    setIsCustomizing(false);
    if (onAssetsConfigured && customAssets) {
      onAssetsConfigured(customAssets);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    if (currentVariant?.defaultAssets) {
      setCustomAssets({ ...currentVariant.defaultAssets });
    }
  };

  // Scroll tabs
  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Estimate time based on total pieces
  const estimateTime = () => {
    if (!assetTotals) return '0';
    const minutes = Math.round(assetTotals.totalPieces * 0.5);
    return `${minutes}-${minutes + 5}`;
  };

  return (
    <div data-testid="strategy-step" className="space-y-6">
      {/* First-time user hint */}
      <FirstTimeHint
        id="strategy_step_intro"
        message="Elige qué tipo de funnel necesitas. Si no estás seguro, 'Lanzamiento' es la opción más versátil para vender productos o servicios digitales."
      />

      {/* Header */}
      <div>
        <h2 className="font-display text-2xl font-bold text-text-main mb-2">
          Elige tu Estrategia
        </h2>
        <p className="text-text-secondary">
          Selecciona el tipo de funnel y personaliza las piezas a generar
        </p>
      </div>

      {/* Strategy Tabs with Scroll */}
      <div className="relative group">
        {/* Left scroll button */}
        <button
          onClick={() => scrollTabs('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-surface/90 backdrop-blur border border-border-subtle rounded-full flex items-center justify-center text-text-muted hover:text-text-main hover:border-brand/50 transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        {/* Tabs container */}
        <div 
          ref={tabsRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 px-1 scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {FUNNEL_STRATEGIES.map((strategy) => (
            <button
              key={strategy.id}
              onClick={() => handleStrategyClick(strategy.id)}
              data-testid={`strategy-tab-${strategy.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                activeStrategy === strategy.id
                  ? 'bg-brand-500 text-white shadow-lg shadow-brand/25'
                  : 'bg-surface border border-border-subtle text-text-secondary hover:border-brand/50 hover:text-text-main'
              }`}
            >
              <span className="text-base">{strategy.icon}</span>
              <span>{strategy.label}</span>
            </button>
          ))}
        </div>
        
        {/* Right scroll button */}
        <button
          onClick={() => scrollTabs('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-surface/90 backdrop-blur border border-border-subtle rounded-full flex items-center justify-center text-text-muted hover:text-text-main hover:border-brand/50 transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Strategy Description */}
      {currentStrategy && (
        <div className="bg-gradient-to-r from-surface/80 to-surface/40 border border-border-subtle rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${currentStrategy.color}15` }}
            >
              {currentStrategy.icon}
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-text-main">
                {currentStrategy.label}
              </h3>
              <p className="text-sm text-text-secondary">{currentStrategy.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column: Variants (60%) */}
        <div className="lg:col-span-3 space-y-4">
          <h4 className="text-sm font-medium text-text-muted uppercase tracking-wider">
            Elige tu variante ({currentStrategy?.variants.length || 0} opciones)
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
            {currentStrategy?.variants.map((variant) => {
              const isSelected = activeVariant === variant.id;
              const totals = calculateAssetTotals(variant.defaultAssets);
              
              return (
                <button
                  key={variant.id}
                  onClick={() => handleVariantClick(variant.id)}
                  data-testid={`variant-${variant.id}`}
                  className={`relative text-left p-5 rounded-xl border-2 transition-all group ${
                    isSelected
                      ? 'border-brand-500 bg-brand/5 shadow-lg shadow-brand/10'
                      : 'border-border-subtle bg-surface hover:border-brand/40 hover:bg-surface/80'
                  }`}
                >
                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle2 className="w-5 h-5 text-brand-500" />
                    </div>
                  )}
                  
                  {/* Variant info */}
                  <div className="space-y-2 pr-6">
                    <h5 className={`font-semibold text-base ${isSelected ? 'text-brand' : 'text-text-main'}`}>
                      {variant.label}
                    </h5>
                    <p className="text-xs text-text-secondary line-clamp-2">
                      {variant.description}
                    </p>
                    
                    {/* Quick stats */}
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      <span className="inline-flex items-center gap-1 text-xs text-text-muted bg-app-bg px-2 py-0.5 rounded">
                        <FileText className="w-3 h-3" />
                        {totals?.landingsCount}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-text-muted bg-app-bg px-2 py-0.5 rounded">
                        <Mail className="w-3 h-3" />
                        {totals?.emailsCount}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-text-muted bg-app-bg px-2 py-0.5 rounded">
                        <MessageCircle className="w-3 h-3" />
                        {totals?.whatsappCount}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-text-muted bg-app-bg px-2 py-0.5 rounded">
                        <Image className="w-3 h-3" />
                        {totals?.adCreativesCount}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Column: Assets Summary (40%) */}
        <div className="lg:col-span-2">
          <div className={`sticky top-4 rounded-xl border-2 transition-all ${
            activeVariant 
              ? 'border-brand/30 bg-gradient-to-br from-brand/5 to-transparent' 
              : 'border-border-subtle bg-surface'
          }`}>
            <div className="p-5 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className={`w-5 h-5 ${activeVariant ? 'text-brand' : 'text-text-muted'}`} />
                  <h4 className="font-semibold text-text-main">Tu funnel incluirá</h4>
                </div>
                
                {activeVariant && !isCustomizing && (
                  <button
                    onClick={() => setIsCustomizing(true)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-brand-500 transition-colors px-2 py-1 rounded hover:bg-brand/5"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    Personalizar
                  </button>
                )}
              </div>

              {activeVariant && currentAssets ? (
                <>
                  {/* Assets List */}
                  <div className="space-y-3">
                    {/* Landings (not editable, just display) */}
                    <div className="flex items-start gap-3 p-3 bg-app-bg/50 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-text-main text-sm">
                          {assetTotals?.landingsCount} Landing Pages
                        </p>
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                          {assetTotals?.landingsList.join(', ')}
                        </p>
                      </div>
                    </div>

                    {/* Emails */}
                    <AssetRow
                      icon={<Mail className="w-4 h-4 text-purple-500" />}
                      iconBg="bg-purple-500/10"
                      label="Emails"
                      value={customAssets?.emails || 0}
                      isEditing={isCustomizing}
                      onChange={(delta) => handleAssetChange('emails', delta)}
                      limits={ASSET_LIMITS.emails}
                    />

                    {/* WhatsApp */}
                    <AssetRow
                      icon={<MessageCircle className="w-4 h-4 text-green-500" />}
                      iconBg="bg-green-500/10"
                      label="Mensajes WhatsApp"
                      value={customAssets?.whatsapp || 0}
                      isEditing={isCustomizing}
                      onChange={(delta) => handleAssetChange('whatsapp', delta)}
                      limits={ASSET_LIMITS.whatsapp}
                    />

                    {/* Ad Copies */}
                    <AssetRow
                      icon={<Type className="w-4 h-4 text-amber-500" />}
                      iconBg="bg-amber-500/10"
                      label="Copies de Ads"
                      value={customAssets?.adCopies || 0}
                      isEditing={isCustomizing}
                      onChange={(delta) => handleAssetChange('adCopies', delta)}
                      limits={ASSET_LIMITS.adCopies}
                    />

                    {/* Ad Creatives */}
                    <AssetRow
                      icon={<Image className="w-4 h-4 text-orange-500" />}
                      iconBg="bg-orange-500/10"
                      label="Creatividades"
                      value={customAssets?.adCreatives || 0}
                      isEditing={isCustomizing}
                      onChange={(delta) => handleAssetChange('adCreatives', delta)}
                      limits={ASSET_LIMITS.adCreatives}
                    />

                    {/* Cart Hours - only show if variant has cartHours */}
                    {customAssets?.cartHours !== undefined && (
                      <AssetRow
                        icon={<Timer className="w-4 h-4 text-red-500" />}
                        iconBg="bg-red-500/10"
                        label="Duración de la Oferta"
                        value={customAssets?.cartHours || 24}
                        suffix="h"
                        isEditing={isCustomizing}
                        onChange={(delta) => handleAssetChange('cartHours', delta)}
                        limits={ASSET_LIMITS.cartHours}
                      />
                    )}
                  </div>

                  {/* Customization buttons */}
                  {isCustomizing && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={resetToDefaults}
                        className="flex-1 text-text-muted hover:text-text-main text-xs"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Resetear
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveCustomization}
                        className="flex-1 bg-brand-500 hover:bg-brand-hover text-white text-xs"
                      >
                        <Save className="w-3.5 h-3.5 mr-1.5" />
                        Guardar
                      </Button>
                    </div>
                  )}

                  {/* Time estimate & total */}
                  <div className="pt-3 border-t border-border-subtle space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-muted flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        Tiempo estimado
                      </span>
                      <span className="text-text-main font-medium">{estimateTime()} min</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-muted">Total de piezas</span>
                      <span className="text-brand-500 font-bold">{assetTotals?.totalPieces}</span>
                    </div>
                  </div>
                </>
              ) : (
                /* Empty state */
                <div className="py-8 text-center">
                  <div className="w-12 h-12 rounded-xl bg-app-bg flex items-center justify-center mx-auto mb-3">
                    <ChevronRight className="w-6 h-6 text-text-muted" />
                  </div>
                  <p className="text-sm text-text-muted">
                    Selecciona una variante para ver y personalizar los recursos
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Asset Row Component for customization
const AssetRow = ({ icon, iconBg, label, value, suffix = '', isEditing, onChange, limits }) => {
  const canDecrease = value > limits.min;
  const canIncrease = value < limits.max;

  return (
    <div className="flex items-center gap-3 p-3 bg-app-bg/50 rounded-lg">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-main text-sm">{label}</p>
      </div>
      
      {isEditing ? (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onChange(-1)}
            disabled={!canDecrease}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
              canDecrease 
                ? 'bg-surface border border-border-subtle text-text-main hover:border-brand/50 hover:text-brand' 
                : 'bg-app-bg text-text-muted/30 cursor-not-allowed'
            }`}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="w-12 text-center font-bold text-brand-500">{value}{suffix}</span>
          <button
            onClick={() => onChange(1)}
            disabled={!canIncrease}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
              canIncrease 
                ? 'bg-surface border border-border-subtle text-text-main hover:border-brand/50 hover:text-brand' 
                : 'bg-app-bg text-text-muted/30 cursor-not-allowed'
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <span className="font-bold text-brand-500">{value}{suffix}</span>
      )}
    </div>
  );
};

export default StrategyStep;
